import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Help as HelpIcon,
  Article as ArticleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const HelpModal = ({ open, onClose, helpType = 'leave-guide' }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  // Force refresh to clear any cached references

  useEffect(() => {
    if (open && helpType) {
      loadHelpContent(helpType);
    }
  }, [open, helpType]);

  const loadHelpContent = async (type) => {
    setLoading(true);
    try {
      let markdownContent = '';

      if (type === 'leave-guide') {
        // Load markdown via backend API (reads private GitHub with PAT from Key Vault)
        const data = await api.get('/help/leave-guide');
        markdownContent = typeof data === 'string' ? data : (data?.content ? String(data.content) : '');
      } else if (type === 'equipment-guide') {
        // Load equipment guide markdown via backend API
        const data = await api.get('/help/equipment-guide');
        markdownContent = typeof data === 'string' ? data : (data?.content ? String(data.content) : '');
      } else if (type === 'sick-leave-guide') {
        // Load sick leave guide markdown via backend API
        const data = await api.get('/help/sick-leave-guide');
        markdownContent = typeof data === 'string' ? data : (data?.content ? String(data.content) : '');
      } else if (type === 'short-absence-guide') {
        // Load short absence guide markdown via backend API
        const data = await api.get('/help/short-absence-guide');
        markdownContent = typeof data === 'string' ? data : (data?.content ? String(data.content) : '');
      }

      setContent(markdownContent);
    } catch (error) {
      console.error('Failed to load help content:', error);
      setContent('Help content could not be loaded. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  const refreshContent = () => {
    if (helpType) {
      loadHelpContent(helpType);
    }
  };

  const getHelpTitle = (type) => {
    switch (type) {
      case 'leave-guide':
        return 'Leave Request User Guide';
      case 'equipment-guide':
        return 'Equipment Request User Guide';
      case 'sick-leave-guide':
        return 'Sick Leave User Guide';
      case 'short-absence-guide':
        return 'Short Absence User Guide';
      case 'system-guide':
        return 'System User Guide';
      default:
        return 'Help & Documentation';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleIcon color="primary" />
          <Typography variant="h6">{getHelpTitle(helpType)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={refreshContent}
            size="small"
            title="Refresh content"
            disabled={loading}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ 
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
        },
      }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Loading help content...</Typography>
          </Box>
        ) : (
          <Box sx={{
            py: 1,
            '& h1': {
              fontSize: '2rem',
              fontWeight: 700,
              color: 'primary.main',
              marginBottom: '1rem',
              marginTop: '1.5rem',
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              paddingBottom: '0.5rem'
            },
            '& h2': {
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'text.primary',
              marginBottom: '0.75rem',
              marginTop: '2rem',
              borderBottom: '1px solid',
              borderColor: 'divider',
              paddingBottom: '0.25rem'
            },
            '& h3': {
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'text.primary',
              marginBottom: '0.5rem',
              marginTop: '1.5rem'
            },
            '& p': {
              marginBottom: '1rem',
              lineHeight: 1.6,
              color: 'text.primary'
            },
            '& ul': {
              marginBottom: '1rem',
              paddingLeft: '1.5rem'
            },
            '& ol': {
              marginBottom: '1rem',
              paddingLeft: '1.5rem'
            },
            '& li': {
              marginBottom: '0.5rem',
              lineHeight: 1.6
            },
            '& strong': {
              fontWeight: 600,
              color: 'text.primary'
            },
            '& em': {
              fontStyle: 'italic',
              color: 'text.secondary'
            },
            '& code': {
              backgroundColor: 'action.hover',
              padding: '0.125rem 0.25rem',
              borderRadius: '0.25rem',
              fontFamily: 'monospace',
              fontSize: '0.875rem'
            },
            '& blockquote': {
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              paddingLeft: '1rem',
              marginLeft: 0,
              marginBottom: '1rem',
              fontStyle: 'italic',
              backgroundColor: 'action.hover',
              padding: '1rem'
            }
          }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({children}) => (
                  <Typography variant="h4" sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: 'primary.main',
                    mb: 2,
                    mt: 3,
                    borderBottom: '2px solid',
                    borderColor: 'primary.main',
                    pb: 1
                  }}>
                    {children}
                  </Typography>
                ),
                h2: ({children}) => (
                  <Typography variant="h5" sx={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1.5,
                    mt: 3,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 0.5
                  }}>
                    {children}
                  </Typography>
                ),
                h3: ({children}) => (
                  <Typography variant="h6" sx={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1,
                    mt: 2
                  }}>
                    {children}
                  </Typography>
                ),
                p: ({children}) => (
                  <Typography variant="body1" sx={{
                    mb: 2,
                    lineHeight: 1.7,
                    color: 'text.primary'
                  }}>
                    {children}
                  </Typography>
                ),
                ul: ({children}) => (
                  <Box component="ul" sx={{
                    mb: 2,
                    pl: 3,
                    '& li': {
                      mb: 0.5,
                      lineHeight: 1.6
                    }
                  }}>
                    {children}
                  </Box>
                ),
                ol: ({children}) => (
                  <Box component="ol" sx={{
                    mb: 2,
                    pl: 3,
                    '& li': {
                      mb: 0.5,
                      lineHeight: 1.6
                    }
                  }}>
                    {children}
                  </Box>
                ),
                strong: ({children}) => (
                  <Box component="strong" sx={{
                    fontWeight: 600,
                    color: 'primary.main'
                  }}>
                    {children}
                  </Box>
                ),
                em: ({children}) => (
                  <Box component="em" sx={{
                    fontStyle: 'italic',
                    color: 'text.secondary'
                  }}>
                    {children}
                  </Box>
                ),
                code: ({children}) => (
                  <Box component="code" sx={{
                    backgroundColor: 'action.hover',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    color: 'primary.main'
                  }}>
                    {children}
                  </Box>
                )
              }}
            >
              {content}
            </ReactMarkdown>
          </Box>
        )}
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Chip 
          icon={<HelpIcon />} 
          label="Need more help? Contact support" 
          variant="outlined" 
          size="small"
        />
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HelpModal;
